import { useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Camera, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { postForm } from "@/lib/api";
import { postJson, useAuth } from "@/lib/auth";
import { useLang } from "@/lib/i18n";
import { COUNTRY_CODES, TURTLES, countryName, flagOf } from "@/data/profile";
import { Avatar } from "@/components/community/shared";

const STEPS = ["photo", "country", "since", "turtle", "story"];

/** Profile fields a member can fill; used for the completion meter too. */
export const profileProgress = (m) => [m?.picture, m?.country, m?.fan_since, m?.favorite, m?.bio].filter(Boolean).length;

const PhotoStep = ({ member, setMember }) => {
  const { t } = useLang();
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      setMember(await postForm("/members/me/avatar", { file }));
      toast.success(t("prof.photoSaved"));
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex flex-wrap items-center gap-6">
      <div className="profile-ring"><Avatar author={member} size={112} /></div>
      <label className="btn-ghost cursor-pointer" data-testid="quiz-photo-upload">
        {busy ? <Loader2 size={15} className="animate-spin" /> : <Camera size={15} />} {t(member.picture ? "prof.changePhoto" : "quiz.photo.btn")}
        <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
      </label>
    </div>
  );
};

const CountryStep = ({ v, set }) => {
  const { lang } = useLang();
  const countries = COUNTRY_CODES.map((c) => [c, countryName(c, lang)]).sort((a, b) => a[1].localeCompare(b[1], lang));
  return (
    <select value={v.country} onChange={(e) => set("country", e.target.value)} className="support-input max-w-md !text-lg" data-testid="quiz-country-select">
      <option value="">—</option>
      {countries.map(([c, n]) => <option key={c} value={c}>{flagOf(c)} {n}</option>)}
    </select>
  );
};

const SinceStep = ({ v, set }) => (
  <input type="number" inputMode="numeric" min={1984} max={new Date().getFullYear()} value={v.fan_since} onChange={(e) => set("fan_since", e.target.value)} placeholder="1990" className="support-input font-display max-w-[14rem] !text-4xl" data-testid="quiz-fan-since-input" />
);

const TurtleStep = ({ v, set }) => (
  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
    {Object.entries(TURTLES).map(([k, x]) => {
      const on = v.favorite === k;
      return (
        <button key={k} type="button" onClick={() => set("favorite", on ? "" : k)} className="rounded-2xl border px-4 py-6 text-left transition-[background-color,border-color,transform] duration-200 hover:-translate-y-0.5"
          style={{ borderColor: on ? x.color : "rgba(255,255,255,.12)", background: on ? x.color : "rgba(255,255,255,.03)", color: on ? "#0d0c0a" : undefined }} data-testid={`quiz-turtle-${k}`}>
          <span className="block h-3 w-10 rounded-full" style={{ background: on ? "#0d0c0a" : x.color }} />
          <span className="font-display mt-4 block text-2xl leading-none">{x.name}</span>
        </button>
      );
    })}
  </div>
);

const StoryStep = ({ v, set }) => {
  const { t } = useLang();
  return <textarea value={v.bio} onChange={(e) => set("bio", e.target.value)} maxLength={2000} rows={7} placeholder={t("prof.bioPh")} className="support-input resize-none" data-testid="quiz-bio-input" />;
};

const BODY = { photo: PhotoStep, country: CountryStep, since: SinceStep, turtle: TurtleStep, story: StoryStep };

export default function ProfileQuiz() {
  const { t } = useLang();
  const { member, setMember } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [i, setI] = useState(0);
  const [busy, setBusy] = useState(false);
  const [v, setV] = useState(null);
  if (member === null) return <main className="grid min-h-screen place-items-center"><Loader2 className="animate-spin" /></main>;
  if (!member) return <Navigate to="/entrar?next=/perfil/cuestionario" replace />;
  const vals = v ?? { country: member.country || "", fan_since: member.fan_since ?? "", favorite: member.favorite || "", bio: member.bio || "" };
  const set = (k, val) => setV({ ...vals, [k]: val });
  const step = STEPS[i];
  const Body = BODY[step];
  const last = i === STEPS.length - 1;
  const finish = async () => {
    setBusy(true);
    try {
      const name = (member.name || member.email.split("@")[0]).padEnd(2, "_");
      setMember(await postJson("/members/me", { ...vals, name, fan_since: vals.fan_since ? Number(vals.fan_since) : null, collection_public: member.collection_public !== false }, "PATCH"));
      toast.success(t("quiz.done"));
      navigate(params.get("next") || "/perfil", { replace: true });
    } catch (e) {
      toast.error(e.message);
      setBusy(false);
    }
  };
  return (
    <main className="px-5 pb-28 pt-32 md:px-10" data-testid="profile-quiz-page">
      <div className="mx-auto max-w-3xl">
        <div className="flex items-center justify-between gap-4">
          <p className="mono text-[11px] uppercase tracking-[0.22em] text-[var(--muted)]" data-testid="quiz-step-count">{t("quiz.step", { n: i + 1, total: STEPS.length })}</p>
          <button type="button" onClick={finish} disabled={busy} className="mono text-[11px] uppercase tracking-[0.18em] text-[var(--muted)] hover:text-[var(--ink)]" data-testid="quiz-skip-all-btn">{t("quiz.later")}</button>
        </div>
        <div className="mt-4 h-1.5 overflow-hidden rounded-full bg-white/10" data-testid="quiz-progress">
          <div className="h-full rounded-full bg-[var(--green)] transition-[width] duration-500" style={{ width: `${((i + 1) / STEPS.length) * 100}%` }} />
        </div>
        <motion.section key={step} initial={{ opacity: 0.2, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }} className="mt-14" data-testid={`quiz-step-${step}`}>
          {i === 0 && <p className="mb-3 text-sm text-[var(--green)]" data-testid="quiz-welcome">{t("quiz.welcome", { name: member.name || "" })}</p>}
          <h1 className="font-display text-4xl leading-[0.95] sm:text-5xl lg:text-6xl">{t(`quiz.${step}.q`)}</h1>
          <p className="mt-4 max-w-xl text-sm text-[var(--muted)] md:text-base">{t(`quiz.${step}.hint`)}</p>
          <div className="mt-10"><Body member={member} setMember={setMember} v={vals} set={set} /></div>
        </motion.section>
        <div className="mt-14 flex flex-wrap items-center justify-between gap-3">
          <button type="button" onClick={() => setI(i - 1)} disabled={i === 0 || busy} className="btn-ghost disabled:opacity-30" data-testid="quiz-back-btn"><ArrowLeft size={15} /> {t("quiz.back")}</button>
          <div className="flex flex-wrap gap-3">
            {!last && <button type="button" onClick={() => setI(i + 1)} className="btn-ghost" data-testid="quiz-skip-btn">{t("quiz.skip")}</button>}
            <button type="button" onClick={last ? finish : () => setI(i + 1)} disabled={busy} className="btn-primary" data-testid={last ? "quiz-finish-btn" : "quiz-next-btn"}>
              {busy ? <Loader2 size={15} className="animate-spin" /> : last ? <Check size={15} /> : <ArrowRight size={15} />} {t(last ? "quiz.finish" : "quiz.next")}
            </button>
          </div>
        </div>
      </div>
    </main>
  );
}
